import { ArrowRight, ArrowUpRight, Check, MapPin, PackageCheck, Phone, Ruler, Wrench } from "lucide-react";
import React from "react";
import { Link, useRoute } from "wouter";
import { QuoteBand } from "@/components/QuoteBand";
import { StructuredData } from "@/components/StructuredData";
import { getCityProjectEvidence, getCityServiceArea, getCityServiceAreasForState, getServiceStateForCityArea } from "@/data/cityServiceAreas";
import { materials, siteConfig } from "@/data/site";
import NotFound from "./NotFound";

export default function CityServiceArea() {
  const [, params] = useRoute<{ stateSlug: string; citySlug: string }>("/service-areas/:stateSlug/:citySlug");
  const area = getCityServiceArea(params?.stateSlug, params?.citySlug);
  const state = getServiceStateForCityArea(params?.stateSlug ?? "");

  if (!area || !state) return <NotFound />;

  const canonicalPath = `/service-areas/${area.stateSlug}/${area.citySlug}`;
  const statePath = `/service-areas/${area.stateSlug}`;
  const url = `${siteConfig.url}${canonicalPath}`;
  const quoteHref = `/request-a-quote?serviceArea=${area.stateSlug}`;
  const projectEvidence = getCityProjectEvidence(area);
  const otherMarkets = getCityServiceAreasForState(area.stateSlug).filter((entry) => entry.route !== canonicalPath);
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
          { "@type": "ListItem", position: 2, name: "Service Areas", item: `${siteConfig.url}/service-areas` },
          { "@type": "ListItem", position: 3, name: state.name, item: `${siteConfig.url}${statePath}` },
          { "@type": "ListItem", position: 4, name: area.city, item: url },
        ],
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        name: area.metaTitle,
        description: area.metaDescription,
        url,
        isPartOf: { "@id": `${siteConfig.url}/#website` },
        about: { "@id": `${url}#service` },
        breadcrumb: { "@id": `${url}#breadcrumb` },
      },
      {
        "@type": "Service",
        "@id": `${url}#service`,
        name: `Commercial bathroom partition supply and installation review in ${area.city}, ${area.stateName}`,
        serviceType: "Commercial bathroom partition and restroom accessory supply with project-specific installation review",
        description: `Partition Stalls reviews commercial restroom partition, toilet stall, and restroom accessory requests for ${area.city}, ${area.stateName} projects. Final scope, pricing, access, scheduling, availability, and installation conditions are confirmed per project.`,
        provider: { "@id": `${siteConfig.url}/#business` },
        areaServed: { "@type": "City", name: `${area.city}, ${area.stateName}` },
        url,
      },
    ],
  };

  return (
    <>
      <StructuredData data={structuredData} />

      <header className="city-area-hero section-line">
        <div className="city-area-hero__copy">
          <p className="breadcrumb"><Link href="/">Home</Link><span>/</span><Link href="/service-areas">Service areas</Link><span>/</span><Link href={statePath}>{state.name}</Link><span>/</span>{area.city}</p>
          <span className="kicker">{area.city} · Commercial restroom projects</span>
          <h1>{`Commercial bathroom partitions in ${area.city}.`}</h1>
          <p>Partition Stalls reviews commercial restroom partition, toilet stall, and accessory requests in {area.city}. Share the project information you have, and the team will review the material, mounting, delivery, and installation direction around the actual site.</p>
          <div className="button-row">
            <Link href={quoteHref} className="button button--primary">Request a {area.city} Quote <ArrowUpRight aria-hidden="true" /></Link>
            <a href={`tel:${siteConfig.phoneHref}`} className="button button--outline"><Phone aria-hidden="true" /> Call {siteConfig.phoneDisplay}</a>
          </div>
          <p className="city-area-hero__scope">Full-service supply and installation requests are reviewed in {state.name}. Final scope, pricing, availability, schedule, access, and site conditions are confirmed per project.</p>
        </div>
        <aside className="city-area-hero__index" aria-label={`${area.city} service area summary`}>
          <span>{state.abbreviation}</span>
          <img src={state.icon} alt={`Outline map of ${state.name}`} width="160" height="160" fetchPriority="high" />
          <div>
            <small>Project location</small>
            <strong>{area.city}</strong>
            <p>Share the exact jobsite, plans or measurements, material direction, quantities, and access conditions.</p>
          </div>
        </aside>
      </header>

      <section className="city-area-start section-line" aria-labelledby="city-start-title">
        <article>
          <Wrench aria-hidden="true" />
          <span className="kicker">Start with the real building</span>
          <h2 id="city-start-title">A useful local quote begins with the site.</h2>
          <p>Material selection, mounting direction, delivery, and installation planning depend on the actual restroom, not the city name alone. The location is the starting point for a project-specific review.</p>
        </article>
        <article>
          <PackageCheck aria-hidden="true" />
          <span className="kicker">One coordinated restroom scope</span>
          <h2>Partitions, privacy, mounting, and accessories.</h2>
          <p>Review the five partition materials with privacy goals, support conditions, hardware, grab bars, dispensers, mirrors, waste units, and other commercial restroom accessories before final pricing is requested.</p>
          <div className="city-area-start__links"><Link href="/bathroom-partitions">Compare materials <ArrowRight aria-hidden="true" /></Link><Link href="/commercial-restroom-accessories">Browse accessories <ArrowRight aria-hidden="true" /></Link></div>
        </article>
      </section>

      {projectEvidence ? (
        <section className="city-area-start section-line" aria-labelledby="city-project-title">
          <article>
            <span className="kicker">{projectEvidence.project.kicker}</span>
            <h2 id="city-project-title">A documented project in {area.city}.</h2>
            <p>{projectEvidence.project.summary}</p>
            <div className="city-area-start__links">
              <Link href={projectEvidence.href}>View the project record and photographs <ArrowRight aria-hidden="true" /></Link>
            </div>
          </article>
          <article>
            <span className="kicker">Evidence and limitations</span>
            <h2>What the project record does not claim.</h2>
            <p>{projectEvidence.project.disclosure}</p>
          </article>
        </section>
      ) : null}

      <section className="city-area-context section-line" aria-labelledby="city-context-title">
        <div className="city-area-section-heading">
          <div><span className="kicker">Local planning context</span><h2 id="city-context-title">Plan the restroom around the building it serves.</h2></div>
          <p>{area.marketContext}</p>
        </div>
        <div className="city-area-facility-grid" aria-label={`${area.city} commercial facility contexts`}>
          {area.facilityTypes.map((facility, index) => <article key={facility}><span>0{index + 1}</span><h3>{facility}</h3></article>)}
        </div>
      </section>

      <section className="city-area-quote-kit section-line" aria-labelledby="city-quote-title">
        <div className="city-area-quote-kit__intro"><Ruler aria-hidden="true" /><span className="kicker kicker--light">{area.city} quote preparation</span><h2 id="city-quote-title">Send the details that change the plan.</h2><p>Complete information supports a more focused material, mounting, delivery, and project-scope discussion. The team confirms the final requirements around the actual building.</p><a href={`tel:${siteConfig.phoneHref}`}><Phone aria-hidden="true" /> {siteConfig.phoneDisplay}</a></div>
        <ol>{area.planningInputs.map((item, index) => <li key={item}><span>0{index + 1}</span><p>{item}</p><Check aria-hidden="true" /></li>)}</ol>
      </section>

      <section className="city-area-materials section-line" aria-labelledby="city-materials-title">
        <div><span className="kicker">Commercial restroom partition materials</span><h2 id="city-materials-title">Compare five material directions for the project.</h2><p>Use the material guides to narrow a starting direction. Final selection depends on the project’s use, finish requirements, conditions, manufacturer system, and approved documents.</p></div>
        <nav aria-label={`${area.city} commercial bathroom partition material guides`}>
          {materials.map((material) => <Link key={material.slug} href={`/bathroom-partitions/${material.slug}`}><span>Material guide</span><strong>{material.name}</strong><ArrowRight aria-hidden="true" /></Link>)}
        </nav>
      </section>

      <section className="city-area-nearby section-line" aria-labelledby="city-nearby-title">
        <div><span className="kicker">Location detail</span><h2 id="city-nearby-title">Nearby project locations to describe in your request.</h2><p>These are geographic reference points, not separate offices or guaranteed service zones. Provide the exact project address so scope and feasibility can be reviewed.</p></div>
        <ul>{area.nearbyAreas.map((place) => <li key={place}><MapPin aria-hidden="true" /> {place}</li>)}</ul>
      </section>

      <aside className="city-area-sources section-line" aria-labelledby="city-sources-title">
        <div><span className="kicker">Market context, not project claims</span><h2 id="city-sources-title">Why these facility settings appear on this page.</h2><p>These public sources describe local building and transportation context. They do not indicate that Partition Stalls supplied or installed any referenced project, or that the listed entities endorse the company.</p></div>
        <div>{area.references.map((reference, index) => <a key={reference.url} href={reference.url} target="_blank" rel="noreferrer"><span>Source 0{index + 1}</span><strong>{reference.label}</strong><p>{reference.context}</p><ArrowUpRight aria-hidden="true" /></a>)}</div>
      </aside>

      <section className="city-area-related section-line" aria-labelledby="city-related-title">
        <div><span className="kicker">Continue planning</span><h2 id="city-related-title">{state.name} service pages.</h2><p>Use the state guide for documented project evidence and broader planning context. Other market pages remain project-specific and do not expand the service boundary.</p></div>
        <nav aria-label={`Other ${state.name} project locations`}>
          <Link href={statePath}><span>{state.name}</span>Open the full-service state guide <ArrowRight aria-hidden="true" /></Link>
          {otherMarkets.slice(0, 2).map((market) => <Link key={market.route} href={market.route}><span>Other project location</span>{market.city} commercial restroom planning <ArrowRight aria-hidden="true" /></Link>)}
        </nav>
      </section>

      <QuoteBand quoteHref={quoteHref} title={`Request a commercial bathroom partition quote for ${area.city}.`} copy="Send the project address, plans or measurements, material direction, quantities, accessory scope, schedule, and site conditions. Call or submit the form to start a project-specific review." />
    </>
  );
}
